import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription, of, switchMap, takeUntil } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { formatSelfServiceNumber, formatStat, snakeCaseToTitleCase } from '../../utils/format.utils';

@Component({
  selector: 'app-number-stats-shared',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './number-stats.component.html',
  styleUrl: './number-stats.component.scss',
})
export class NumberStatsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data!: any;

  stat!: string;
  statInputValue: number | string | null = null;

  private filterSub!: Subscription;
  private unSubscribe$ = new Subject<void>();

  public hasError = false;
  public errorMessage = '';

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || changes['data'].firstChange) return;
    this.loadStat(this.getInitialFiltersForWidget())
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => this.updateStat(res),
        error: (err: any) => console.error('Error loading stat:', err),
      });
  }

  /** ngOnInit, not ngAfterViewInit: `loadStat` resolves synchronously on a cache
   *  hit, so setting `stat` after the first check throws NG0100. */
  ngOnInit(): void {
    this.filterSub = this.loadStat(this.getInitialFiltersForWidget())
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => this.updateStat(res),
        error: (err: any) => {
          console.error('Error loading stat:', err);
        },
      });

    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
            return of(null);
          }

          if (this.data.callApiInDefault === false) {
            let mandatoryFiltersSelected = true;
            const missingMandatoryFilterNames: string[] = [];

            if (this.data.mandatoryFilters && this.data.mandatoryFilters.length > 0) {
              for (const mandatoryFilterKey of this.data.mandatoryFilters) {
                const filterValue =
                  filters.queryParams[mandatoryFilterKey] ||
                  filters.requestParams[mandatoryFilterKey];
                if (!filterValue || filterValue.length === 0) {
                  mandatoryFiltersSelected = false;
                  missingMandatoryFilterNames.push(snakeCaseToTitleCase(mandatoryFilterKey));
                }
              }
            }

            if (!mandatoryFiltersSelected) {
              this.hasError = true;
              this.errorMessage = `Please select ${missingMandatoryFilterNames.join(' and ')} to see the data.`;
              return of(null);
            }
          }

          this.hasError = false;
          return this.loadStat(filters);
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (res === null) return;

          try {
            this.updateStat(res);

            if (!this.stat) {
              this.hasError = true;
              this.errorMessage = 'No data found for the selected filters.';
            } else {
              this.hasError = false;
            }
          } catch {
            this.hasError = true;
            this.errorMessage = 'Error processing data.';
          }
        },
        error: (err: any) => {
          this.hasError = true;
          this.errorMessage = 'Error loading stat.';
          console.error('Error loading stat:', err);
        },
      });
  }

  get formattedTitle(): string {
    return this.data?.title?.replace(/\n/g, '<br>') || '';
  }

  get alignmentClass(): string {
    const alignment = String(this.data?.alignment || this.data?.contentAlignment || 'center').toLowerCase();

    if (alignment === 'left' || alignment === 'start') {
      return 'align-left';
    }

    if (alignment === 'right' || alignment === 'end') {
      return 'align-right';
    }

    return 'align-center';
  }

  /** Title/value arrangement: `row` (default — title left, value right) or
   *  `column` (title on top, value below). Set per-card via the KPI Format
   *  popup ("Layout"); absent falls back to the theme's own direction token. */
  get directionClass(): string {
    const direction = String(this.data?.cardLayout ?? this.data?.direction ?? '').toLowerCase();
    if (direction === 'column' || direction === 'stacked' || direction === 'vertical') return 'dir-column';
    if (direction === 'row' || direction === 'inline' || direction === 'horizontal') return 'dir-row';
    return '';
  }

  get contentClasses(): string {
    return `${this.alignmentClass} ${this.directionClass}`.trim();
  }

  /** Per-card font sizes as CSS custom properties. Each is omitted when unset
   *  so the stylesheet's responsive default (and the tile-wide font size)
   *  still applies — only an explicit pick overrides them. */
  get cardStyle(): Record<string, string> {
    const style: Record<string, string> = {};
    const title = this.toCssSize(this.data?.titleFontSize);
    const value = this.toCssSize(this.data?.valueFontSize);
    const iconColor = String(this.data?.iconColor ?? '').trim();
    const iconSize = this.toCssSize(this.data?.iconSize);
    if (title) style['--dvm-stat-title-font-size'] = title;
    if (value) style['--dvm-stat-value-font-size'] = value;
    if (iconColor) style['--dvm-stat-icon-color'] = iconColor;
    if (iconSize) style['--dvm-stat-icon-size'] = iconSize;
    return style;
  }

  /** A bare number is px; anything else (e.g. "1.5rem") passes through. */
  private toCssSize(raw: unknown): string {
    if (raw === null || raw === undefined || raw === '') return '';
    const text = String(raw).trim();
    if (!text) return '';
    return /^\d*\.?\d+$/.test(text) ? `${text}px` : text;
  }

  /** Material icon name shown before the title (blank hides it). */
  get statIcon(): string {
    return String(this.data?.icon ?? '').trim();
  }

  get hasIcon(): boolean {
    return !!this.statIcon;
  }

  /** Caption under the value, e.g. "gross, unweighted". */
  get subText(): string {
    return String(this.data?.subText ?? '').trim();
  }

  get hasSubText(): boolean {
    return !!this.subText;
  }

  /** Trend text shown on the right of the caption line, e.g. "12.3% vs Q2". */
  get deltaText(): string {
    return String(this.data?.deltaText ?? '').trim();
  }

  get hasDelta(): boolean {
    return !!this.deltaText;
  }

  get hasFooter(): boolean {
    return this.hasSubText || this.hasDelta;
  }

  /** up / down / flat — drives both the arrow glyph and the default colour. */
  private get deltaDirection(): string {
    return String(this.data?.deltaDirection ?? 'up').toLowerCase();
  }

  get deltaArrow(): string {
    if (this.deltaDirection === 'down') return '▼';
    if (this.deltaDirection === 'flat' || this.deltaDirection === 'none') return '';
    return '▲';
  }

  get deltaClass(): string {
    if (this.deltaDirection === 'down') return 'delta-down';
    if (this.deltaDirection === 'flat' || this.deltaDirection === 'none') return 'delta-flat';
    return 'delta-up';
  }

  /** An explicit colour overrides the direction's default green/red. */
  get deltaStyle(): Record<string, string> {
    const color = String(this.data?.deltaColor ?? '').trim();
    return color ? { color } : {};
  }

  get isInputNumberStat(): boolean {
    return this.data?.inputNumberStat === true;
  }

  /** Optional hover-info icon next to the title, configured via the KPI's
   *  Format popup ("Info tooltip text"). Hidden entirely when blank so
   *  existing cards with no tooltip configured render unchanged. */
  get infoText(): string {
    return String(this.data?.infoText ?? '').trim();
  }

  get hasInfoText(): boolean {
    return !!this.infoText;
  }

  /** Where the info icon sits: "corner" (top-right of the card, default) or
   *  "title" (inline next to the title text), set via the KPI's Format popup
   *  ("Info icon position"). */
  get isInfoIconInCorner(): boolean {
    return String(this.data?.infoIconPosition ?? 'corner') !== 'title';
  }

  /** Long values (e.g. a configuration/serial code like "D193102MX03") at the
   *  card's normal 2rem font overflow a narrow KPI tile — this scales the size
   *  down in steps so long text shrinks to fit instead of spilling out. */
  get valueLengthClass(): string {
    const length = String(this.stat ?? '').length;
    if (length > 14) return 'value-len-xl';
    if (length > 8) return 'value-len-lg';
    return '';
  }

  onStatInputChange(value: number | string | null): void {
    this.statInputValue = value;
    this.stat = String(value ?? '');
  }

  onInputStatEnter(): void {
    if (!this.isInputNumberStat) {
      return;
    }

    const currentFilters = this.globalFilterService.getCurrentFilters();
    const requestParams = {
      ...(currentFilters?.requestParams || {}),
      [this.data?.key]: this.statInputValue,
    };

    this.loadStat({
      ...currentFilters,
      requestParams,
      useQueryParams: this.data?.useQueryParams || false,
    })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => this.updateStat(res),
        error: (err: any) => {
          console.error('Error loading stat:', err);
          this.hasError = true;
          this.errorMessage = 'Error loading stat.';
        },
      });
  }

  loadStat(filters?: any): any {
    return this.configService.getConfig(
      this.data?.apiUrl,
      this.data,
      {
        ...filters,
        useQueryParams: this.data?.useQueryParams || false,
      },
      this.data?.method || 'post',
    );
  }

  private getInitialFiltersForWidget(): any | undefined {
    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(this.data?.widgetId);
    if (navigationFilters) {
      return navigationFilters;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const hasFilters = !!(
      Object.keys(filters?.queryParams || {}).length ||
      Object.keys(filters?.requestParams || {}).length ||
      filters?.filterListener
    );

    if (!hasFilters) {
      return undefined;
    }

    return this.chartFilterHandler.shouldReload(filters, this.data?.widgetId) ? filters : undefined;
  }

  private updateStat(res: any): void {
    const rawNormalized = res?.responseList?.[0]?.data?.[0] ?? res?.data?.[0] ?? res;
    const normalized = Array.isArray(rawNormalized) ? rawNormalized[0] : rawNormalized;
    const parent = this.data?.parentKey
      ? normalized?.[this.data?.parentKey] ?? res?.[this.data?.parentKey]
      : normalized;
    const source = parent ?? normalized;
    const rawValue = source?.[this.data.key];

    this.statInputValue = rawValue ?? null;
    this.stat = this.formatValue(rawValue);
  }

  /** A zero-padded numeric STRING is an identifier, not a measure — a part
   *  number, plant code or account like "04033". `Number()` happily parses it
   *  (4033, not NaN), so the NaN guard below can't catch it and number
   *  formatting would silently drop the padding. Detected by a leading zero
   *  followed by another DIGIT, so genuine decimals ("0.75") still format. */
  private isZeroPaddedCode(value: any): boolean {
    return typeof value === 'string' && /^\s*[+-]?0\d/.test(value);
  }

  private formatValue(value: any): string {
    // An identifier that merely looks numeric is shown exactly as the backend
    // sent it — reformatting it as a number would corrupt the value.
    if (this.isZeroPaddedCode(value)) {
      return `${this.data?.prefix ?? ''}${String(value).trim()}${this.data?.suffix ?? ''}`;
    }
    // Self-service KPI cards carry an explicit numberFormat (chosen in Chart
    // Builder) — format with it before falling back to the generic
    // formatStat/String behavior other NumberStatsComponent consumers rely on.
    // Guard against non-numeric values first (e.g. a KPI built on
    // aggregation: DISTINCT, which returns the actual distinct value — often
    // text, like a plant code): Number("DAV") is NaN, so blindly formatting
    // would render blank/"NaN" instead of the real value.
    const numericValue = Number(value);
    if (this.data?.numberFormat && value !== null && value !== undefined && value !== '' && !Number.isNaN(numericValue)) {
      const formatted = formatSelfServiceNumber(
        numericValue,
        this.data.numberFormat,
        this.data?.decimals,
        this.data?.numberScale,
        this.data?.currencySymbol,
      );
      // Free-form prefix/suffix chosen in the KPI's Format popup (e.g. "-",
      // "$", "Δ " / "%", "#", "&") — wrapped around whatever
      // formatSelfServiceNumber produced (which already carries its own "-"
      // for a negative value).
      return `${this.data?.prefix ?? ''}${formatted}${this.data?.suffix ?? ''}`;
    }
    return this.data?.useFormatStat
      ? formatStat(value, this.data?.key || '', this.data?.prefix || '')
      : String(value ?? '');
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
